"use client";

import { Button } from "@/components/ui/button";
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "@/components/ui/hover-card";
import { Spinner } from "@/components/ui/spinner";
import { cn } from "@/lib/utils";
import type { FileUIPart, SourceDocumentUIPart } from "ai";
import {
  FileTextIcon,
  GlobeIcon,
  ImageIcon,
  Music2Icon,
  PaperclipIcon,
  VideoIcon,
  XIcon,
} from "lucide-react";
import { usePdfThumbnail } from "@/hooks/use-pdf-thumbnail";
import Image from "next/image";
import type { ComponentProps, HTMLAttributes, ReactNode } from "react";
import { createContext, useCallback, useContext, useMemo } from "react";

// ============================================================================
// Types
// ============================================================================

export type AttachmentData =
  | (FileUIPart & { id: string; uploading?: boolean })
  | (SourceDocumentUIPart & { id: string });

export type AttachmentMediaCategory =
  | "image"
  | "video"
  | "audio"
  | "document"
  | "source"
  | "unknown";

export type AttachmentVariant = "grid" | "inline" | "list";

const mediaCategoryIcons: Record<AttachmentMediaCategory, typeof ImageIcon> = {
  audio: Music2Icon,
  document: FileTextIcon,
  image: ImageIcon,
  source: GlobeIcon,
  unknown: PaperclipIcon,
  video: VideoIcon,
};

// ============================================================================
// Utility Functions
// ============================================================================

export const getMediaCategory = (
  data: AttachmentData
): AttachmentMediaCategory => {
  if (data.type === "source-document") {
    return "source";
  }

  const mediaType = data.mediaType ?? "";

  if (mediaType.startsWith("image/")) {
    return "image";
  }
  if (mediaType.startsWith("video/")) {
    return "video";
  }
  if (mediaType.startsWith("audio/")) {
    return "audio";
  }
  if (mediaType.startsWith("application/") || mediaType.startsWith("text/")) {
    return "document";
  }

  return "unknown";
};

export const getAttachmentLabel = (data: AttachmentData): string => {
  if (data.type === "source-document") {
    return data.title || data.filename || "Source";
  }

  const category = getMediaCategory(data);
  return data.filename || (category === "image" ? "Image" : "Attachment");
};

export const getFileTypeLabel = (data: AttachmentData): string | null => {
  if (data.type === "source-document") return null;

  // Pasted text: text/plain with no file extension at the end of the filename
  if (
    data.type === "file" &&
    data.mediaType === "text/plain" &&
    data.filename &&
    !/\.[a-zA-Z0-9]{1,6}$/.test(data.filename)
  ) {
    return "PASTED";
  }

  // Prefer extension from filename
  if (data.filename) {
    const dot = data.filename.lastIndexOf(".");
    if (dot !== -1 && dot < data.filename.length - 1) {
      return data.filename.slice(dot + 1).toUpperCase();
    }
  }

  // Fall back to media type subtype (e.g. "application/pdf" → "PDF")
  if (data.mediaType) {
    const slash = data.mediaType.indexOf("/");
    if (slash !== -1) {
      const sub = data.mediaType.slice(slash + 1).split(";")[0].trim();
      // Skip generic subtypes
      if (sub && sub !== "octet-stream") {
        return sub.toUpperCase();
      }
    }
  }

  return null;
};

const renderAttachmentImage = (
  url: string,
  filename: string | undefined,
  isGrid: boolean
) =>
  isGrid ? (
    <Image
      alt={filename || "Image"}
      className="size-full object-cover"
      height={96}
      sizes="96px"
      src={url}
      unoptimized
      width={96}
    />
  ) : (
    <Image
      alt={filename || "Image"}
      className="size-full rounded object-cover"
      height={20}
      sizes="20px"
      src={url}
      unoptimized
      width={20}
    />
  );

// ============================================================================
// Contexts
// ============================================================================

interface AttachmentsContextValue {
  variant: AttachmentVariant;
}

const AttachmentsContext = createContext<AttachmentsContextValue | null>(null);

interface AttachmentContextValue {
  data: AttachmentData;
  mediaCategory: AttachmentMediaCategory;
  onRemove?: () => void;
  variant: AttachmentVariant;
}

const AttachmentContext = createContext<AttachmentContextValue | null>(null);

// ============================================================================
// Hooks
// ============================================================================

export const useAttachmentsContext = () =>
  useContext(AttachmentsContext) ?? { variant: "grid" as const };

export const useAttachmentContext = () => {
  const ctx = useContext(AttachmentContext);
  if (!ctx) {
    throw new Error("Attachment components must be used within <Attachment>");
  }
  return ctx;
};

// ============================================================================
// Attachments - Container
// ============================================================================

export type AttachmentsProps = HTMLAttributes<HTMLDivElement> & {
  variant?: AttachmentVariant;
};

export const Attachments = ({
  variant = "grid",
  className,
  children,
  ...props
}: AttachmentsProps) => {
  const contextValue = useMemo(() => ({ variant }), [variant]);

  return (
    <AttachmentsContext.Provider value={contextValue}>
      <div
        className={cn(
          "flex items-start",
          variant === "list" ? "flex-col gap-2" : "flex-wrap gap-2",
          variant === "grid" && "ml-auto w-fit",
          className
        )}
        {...props}
      >
        {children}
      </div>
    </AttachmentsContext.Provider>
  );
};

// ============================================================================
// Attachment - Item
// ============================================================================

export type AttachmentProps = HTMLAttributes<HTMLDivElement> & {
  data: AttachmentData;
  onRemove?: () => void;
};

export const Attachment = ({
  data,
  onRemove,
  className,
  children,
  ...props
}: AttachmentProps) => {
  const { variant } = useAttachmentsContext();
  const mediaCategory = getMediaCategory(data);

  const contextValue = useMemo<AttachmentContextValue>(
    () => ({ data, mediaCategory, onRemove, variant }),
    [data, mediaCategory, onRemove, variant]
  );

  return (
    <AttachmentContext.Provider value={contextValue}>
      <div
        className={cn(
          "group relative",
          variant === "grid" && "size-24 rounded-lg border border-border",
          variant === "inline" && [
            "flex h-8 cursor-pointer select-none items-center gap-1.5",
            "rounded-md border border-border px-1.5",
            "font-medium text-sm transition-all",
            "hover:bg-accent hover:text-accent-foreground dark:hover:bg-accent/50",
          ],
          variant === "list" && [
            "flex w-full items-center gap-3 rounded-lg border p-3",
            "hover:bg-accent/50",
          ],
          className
        )}
        {...props}
      >
        {children}
      </div>
    </AttachmentContext.Provider>
  );
};

// ============================================================================
// AttachmentPreview - Media preview
// ============================================================================

export type AttachmentPreviewProps = HTMLAttributes<HTMLDivElement> & {
  fallbackIcon?: ReactNode;
};

export const AttachmentPreview = ({
  fallbackIcon,
  className,
  ...props
}: AttachmentPreviewProps) => {
  const { data, mediaCategory, variant } = useAttachmentContext();

  const isPdf =
    data.type === "file" &&
    (data.mediaType === "application/pdf" ||
      (data.filename?.toLowerCase().endsWith(".pdf") ?? false));

  const pdfUrl = isPdf && data.type === "file" ? data.url : undefined;
  const pdfThumbnail = usePdfThumbnail(pdfUrl, isPdf && variant === "grid");

  const iconSize = variant === "inline" ? "size-3" : "size-4";

  const renderIcon = (Icon: typeof ImageIcon) => (
    <Icon className={cn(iconSize, "text-muted-foreground")} />
  );

  const renderContent = () => {
    if (mediaCategory === "image" && data.type === "file" && data.url) {
      return renderAttachmentImage(data.url, data.filename, variant === "grid");
    }

    if (mediaCategory === "video" && data.type === "file" && data.url) {
      return <video className="size-full object-cover" muted src={data.url} />;
    }

    if (isPdf && variant === "grid") {
      if (pdfThumbnail.status === "ready") {
        return (
          // eslint-disable-next-line @next/next/no-img-element
          <img
            alt={data.type === "file" ? (data.filename ?? "PDF") : "PDF"}
            className="size-full object-cover object-top"
            src={pdfThumbnail.dataUrl}
          />
        );
      }
      if (pdfThumbnail.status === "loading") {
        return <Spinner className="size-4" />;
      }
    }

    const Icon = mediaCategoryIcons[mediaCategory];
    return fallbackIcon ?? renderIcon(Icon);
  };

  const isUploading =
    data.type === "file" && (data as FileUIPart & { uploading?: boolean }).uploading;

  return (
    <div
      className={cn(
        "relative flex shrink-0 items-center justify-center overflow-hidden",
        variant === "grid" && "size-full rounded-lg bg-background dark:bg-muted",
        variant === "inline" && "size-5 rounded bg-background",
        variant === "list" && "size-12 rounded bg-muted",
        className
      )}
      {...props}
    >
      {renderContent()}
      {isUploading && (
        <div className="absolute inset-0 flex items-center justify-center bg-background/60">
          <Spinner className="size-4" />
        </div>
      )}
    </div>
  );
};

// ============================================================================
// AttachmentInfo - Name and type display
// ============================================================================

export type AttachmentInfoProps = HTMLAttributes<HTMLDivElement> & {
  showMediaType?: boolean;
};

export const AttachmentInfo = ({
  showMediaType = false,
  className,
  ...props
}: AttachmentInfoProps) => {
  const { data, variant } = useAttachmentContext();
  const label = getAttachmentLabel(data);

  if (variant === "grid") {
    return null;
  }

  return (
    <div className={cn("min-w-0 flex-1", className)} {...props}>
      <span className="block truncate">{label}</span>
      {showMediaType && data.mediaType && (
        <span className="block truncate text-muted-foreground text-xs">
          {data.mediaType}
        </span>
      )}
    </div>
  );
};

// ============================================================================
// AttachmentTypeLabel - File type badge + filename (grid variant, bottom-left)
// ============================================================================

export type AttachmentTypeLabelProps = HTMLAttributes<HTMLDivElement>;

export const AttachmentTypeLabel = ({
  className,
  ...props
}: AttachmentTypeLabelProps) => {
  const { data, mediaCategory, variant } = useAttachmentContext();

  if (variant !== "grid") return null;

  const typeLabel = getFileTypeLabel(data);
  const filename = data.type === "file" ? (data.filename ?? null) : null;

  if (!typeLabel && !filename) return null;

  const isImage = mediaCategory === "image";
  const isPdf =
    data.type === "file" &&
    (data.mediaType === "application/pdf" ||
      (data.filename?.toLowerCase().endsWith(".pdf") ?? false));

  const isPastedText =
    data.type === "file" &&
    data.mediaType === "text/plain" &&
    filename &&
    !/\.[a-zA-Z0-9]{1,6}$/.test(filename);

  if (isPastedText) {
    return (
      <div
        className={cn(
          "absolute inset-0 z-10 flex flex-col gap-1.5 p-2 bg-muted overflow-hidden rounded-lg",
          className
        )}
        {...props}
      >
        <div className="min-h-0 flex-1 overflow-hidden">
          <p className="line-clamp-4 break-words text-[10px] leading-snug text-muted-foreground">
            {filename}
          </p>
        </div>
        <span className="shrink-0 w-fit rounded border border-border bg-background px-1.5 py-0.5 font-semibold text-foreground text-[10px] leading-none tracking-wide">
          PASTED
        </span>
      </div>
    );
  }

  if (isImage || isPdf) {
    // Image / PDF thumbnail: keep preview visible — use gradient scrims only
    return (
      <>
        {filename && (
          <div className="absolute top-0 left-0 right-0 z-10 px-1.5 pt-1.5 pb-5 bg-gradient-to-b from-black/60 to-transparent pointer-events-none">
            <span className="line-clamp-2 break-all text-[10px] leading-snug text-white/90 font-medium">
              {filename}
            </span>
          </div>
        )}
        {typeLabel && (
          <span className="absolute bottom-1.5 left-1.5 z-20 rounded bg-background/80 backdrop-blur-sm px-1 py-0.5 font-semibold text-foreground text-[10px] leading-none tracking-wide pointer-events-none">
            {typeLabel}
          </span>
        )}
      </>
    );
  }

  // Non-image/non-PDF: solid overlay (nothing to see through), same layout
  return (
    <div
      className={cn("absolute inset-0 z-10 flex flex-col justify-between p-2 bg-muted rounded-lg", className)}
      {...props}
    >
      {filename && (
        <span className="line-clamp-3 break-all text-[11px] leading-snug text-foreground font-medium text-left">
          {filename}
        </span>
      )}
      {typeLabel && (
        <span className="w-fit rounded border border-border bg-background px-1.5 py-0.5 font-semibold text-foreground text-[10px] leading-none tracking-wide">
          {typeLabel}
        </span>
      )}
    </div>
  );
};

// ============================================================================
// AttachmentRemove - Remove button
// ============================================================================

export type AttachmentRemoveProps = ComponentProps<typeof Button> & {
  label?: string;
};

export const AttachmentRemove = ({
  label = "Remove",
  className,
  children,
  ...props
}: AttachmentRemoveProps) => {
  const { onRemove, variant } = useAttachmentContext();

  const handleClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      onRemove?.();
    },
    [onRemove]
  );

  if (!onRemove) {
    return null;
  }

  return (
    <Button
      aria-label={label}
      className={cn(
        variant === "grid" && [
          "absolute -top-1 -left-1 size-3 rounded-full p-0 z-30",
          "bg-muted shadow-sm border border-border text-muted-foreground",
          "hover:bg-muted/80",
        ],
        variant === "inline" && [
          "size-5 rounded p-0",
          "opacity-0 transition-opacity group-hover:opacity-100",
        ],
        variant === "list" && ["size-8 shrink-0 rounded p-0"],
        className
      )}
      onClick={handleClick}
      type="button"
      variant="ghost"
      {...props}
    >
      {children ?? (
        <XIcon
          className={cn(
            variant === "grid" && "size-2",
            variant === "inline" && "size-2.5",
            variant === "list" && "size-4"
          )}
        />
      )}
      <span className="sr-only">{label}</span>
    </Button>
  );
};

// ============================================================================
// AttachmentHoverCard - Hover preview
// ============================================================================

export type AttachmentHoverCardProps = ComponentProps<typeof HoverCard>;

export const AttachmentHoverCard = ({
  openDelay = 0,
  closeDelay = 0,
  ...props
}: AttachmentHoverCardProps) => (
  <HoverCard closeDelay={closeDelay} openDelay={openDelay} {...props} />
);

export type AttachmentHoverCardTriggerProps = ComponentProps<
  typeof HoverCardTrigger
>;

export const AttachmentHoverCardTrigger = (
  props: AttachmentHoverCardTriggerProps
) => <HoverCardTrigger {...props} />;

export type AttachmentHoverCardContentProps = ComponentProps<
  typeof HoverCardContent
>;

export const AttachmentHoverCardContent = ({
  align = "start",
  className,
  ...props
}: AttachmentHoverCardContentProps) => (
  <HoverCardContent
    align={align}
    className={cn("w-auto p-2", className)}
    {...props}
  />
);

// ============================================================================
// AttachmentEmpty - Empty state
// ============================================================================

export type AttachmentEmptyProps = HTMLAttributes<HTMLDivElement>;

export const AttachmentEmpty = ({
  className,
  children,
  ...props
}: AttachmentEmptyProps) => (
  <div
    className={cn(
      "flex items-center justify-center p-4 text-muted-foreground text-sm",
      className
    )}
    {...props}
  >
    {children ?? "No attachments"}
  </div>
);
